<script setup lang="ts">
import { ref, computed } from 'vue'
import { state, snippetsForActive, removeSnippet, setPage, revealSnippet, setSnippetComment, setSnippetName } from '../lib/store'
import { navigateToCell } from '../lib/excel'
import { MODE_LABELS } from '../lib/modes'
import type { Snippet } from '../types'

const open = ref(true)
const editing = ref<string | null>(null)
const draft = ref('')
const editingNameId = ref<string | null>(null)
const nameDraft = ref('')
const snips = computed(() => [...snippetsForActive()].reverse())

async function goTo(s: Snippet) {
  setPage(s.pageNumber)
  revealSnippet(s.id)
  if (state.insideOffice && s.cellAddress !== '(preview)') {
    try {
      await navigateToCell(s.worksheetName, s.cellAddress, s.worksheetId)
    } catch {
      /* sheet/cell may no longer exist */
    }
  }
}

function startComment(s: Snippet) {
  editing.value = s.id
  draft.value = s.comment ?? ''
}
function saveComment(s: Snippet) {
  setSnippetComment(s.id, draft.value)
  editing.value = null
}

function startRename(s: Snippet) {
  editingNameId.value = s.id
  nameDraft.value = s.displayName
}
function saveRename(s: Snippet) {
  const name = nameDraft.value.trim()
  if (name) setSnippetName(s.id, name)
  editingNameId.value = null
}

// Match the original: deleting a snippet unlinks it but LEAVES the Excel cell content intact
// (the user chose that cell). It does not clear/overwrite the cell.
function remove(s: Snippet) {
  removeSnippet(s.id)
}
</script>

<template>
  <div class="snips" :class="{ collapsed: !open }">
    <button class="hdr" @click="open = !open">
      <span>{{ open ? '▾' : '▸' }}</span>
      <strong>Snippets</strong>
      <span class="count">{{ snips.length }}</span>
    </button>
    <div v-if="open" class="list scroll">
      <p v-if="!snips.length" class="empty muted">Draw a box on the page to capture your first snip.</p>
      <div
        v-for="s in snips"
        :key="s.id"
        class="item"
        :class="['mode-' + s.mode, { active: s.id === state.highlightedSnippetId }]"
      >
        <div class="row-line">
          <button class="link" :title="s.text" @click="goTo(s)">
            <span class="tag">{{ MODE_LABELS[s.mode] }}</span>
            <span class="name" title="Double-click to rename" @dblclick.stop="startRename(s)">{{ s.displayName }}</span>
            <span class="addr">{{ s.cellAddress }}</span>
          </button>
          <button class="note-btn" title="Rename" @click="startRename(s)">✎</button>
          <button class="note-btn" :class="{ on: !!s.comment }" title="Add / edit comment" @click="startComment(s)">💬</button>
          <button class="x" title="Delete snip (keeps the cell)" @click="remove(s)">×</button>
        </div>
        <div v-if="editingNameId === s.id" class="comment-edit">
          <input
            v-model="nameDraft"
            placeholder="Snippet name…"
            @keyup.enter="saveRename(s)"
            @blur="saveRename(s)"
          />
        </div>
        <div v-if="editing === s.id" class="comment-edit">
          <input
            v-model="draft"
            placeholder="Add a comment…"
            @keyup.enter="saveComment(s)"
            @blur="saveComment(s)"
          />
        </div>
        <p v-else-if="s.comment" class="comment" title="Click to edit" @click="startComment(s)">{{ s.comment }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.snips {
  border-top: 1px solid var(--line);
  background: var(--bg);
  display: flex;
  flex-direction: column;
  max-height: 38%;
}
.snips.collapsed {
  max-height: none;
}
.hdr {
  border: none;
  border-radius: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 6px 8px;
  background: var(--bg-soft);
}
.hdr:hover {
  background: var(--brand-50);
}
.count {
  margin-left: auto;
  background: var(--brand);
  color: #fff;
  border-radius: 10px;
  padding: 0 7px;
  font-size: 11px;
}
.list {
  overflow: auto;
  padding: 4px;
}
.empty {
  padding: 10px;
  text-align: center;
}
.item {
  display: flex;
  flex-direction: column;
  border-left: 3px solid var(--brand);
  margin-bottom: 3px;
  background: var(--bg-soft);
  border-radius: 4px;
}
.item.active {
  outline: 2px solid var(--brand);
  outline-offset: -1px;
}
.row-line {
  display: flex;
  align-items: center;
}
.note-btn {
  border: none;
  background: none;
  padding: 0 4px;
  font-size: 13px;
  opacity: 0.4;
}
.note-btn.on { opacity: 1; }
.note-btn:hover { background: none; opacity: 1; }
.comment-edit {
  padding: 0 8px 6px;
}
.comment-edit input {
  width: 100%;
  padding: 4px 6px;
  border: 1px solid var(--line);
  border-radius: 4px;
  font-size: 12px;
}
.comment {
  margin: 0;
  padding: 0 10px 6px;
  font-size: 11px;
  color: var(--muted);
  cursor: text;
}
.item.mode-sum { border-left-color: #c97b30; }
.item.mode-table { border-left-color: #7b5ea7; }
.item.mode-validation { border-left-color: #5a8a32; }
.item.mode-exception { border-left-color: var(--danger); }
.link {
  border: none;
  background: none;
  border-radius: 0;
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  padding: 5px 6px;
  text-align: left;
}
.link:hover { background: var(--brand-50); }
.tag {
  font-size: 9px;
  text-transform: uppercase;
  color: var(--muted);
  flex: none;
}
.name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.addr {
  font-variant-numeric: tabular-nums;
  color: var(--brand-dark);
  font-weight: 600;
  flex: none;
}
.x {
  border: none;
  background: none;
  color: var(--muted);
  font-size: 16px;
  padding: 0 8px;
}
.x:hover { color: var(--danger); background: none; }
</style>
